import { formatCurrency, formatInteger } from '@/design'
import { GA4_METRIC_KEYS, type Ga4MetricKey, type Ga4TrafficMetrics, type Ga4TrafficReport } from '@/api/types'
import { dayCount, formatWindow } from '@/lib/filters'

export interface MetricDef {
  key: keyof Ga4TrafficMetrics
  header: string
}

/** A header for every `Ga4MetricKey`: a record over the key list, so a metric added to it does not compile without one. */
const HEADERS: Record<Ga4MetricKey, string> = {
  sessions: 'Sessions',
  users: 'Users',
  newUsers: 'New users',
  engagedSessions: 'Engaged sessions',
  conversions: 'Conversions',
  purchases: 'Purchases',
  revenueMinor: 'Revenue',
}

/** The GA4 metrics, in the order the table and the totals show them: the shared key list with a header each. */
export const METRICS: readonly MetricDef[] = GA4_METRIC_KEYS.map((key) => ({ key, header: HEADERS[key] }))

/** Revenue arrives in minor units of the report's currency; this is the one place it becomes major units. */
export function formatRevenue(minor: number | null, currency: string | null): string {
  if (minor === null || currency === null) return '—'
  const exponent = new Intl.NumberFormat('en-GB', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
  return formatCurrency(minor / 10 ** exponent, { currency })
}

export function formatMetric(key: MetricDef['key'], value: number | null, currency: string | null): string {
  if (key === 'revenueMinor') return formatRevenue(value, currency)
  return value === null ? '—' : formatInteger(value)
}

export function windowLabel(from: string, to: string): string {
  return formatWindow({ from, to, days: dayCount(from, to) })
}

/** "10 Aug – 16 Aug 2026 · compared with 3 Aug – 9 Aug 2026" */
export function subtitleFor(report: Ga4TrafficReport): string {
  const current = windowLabel(report.start, report.end)
  if (!report.compared || !report.previousStart || !report.previousEnd) return current
  return `${current} · compared with ${windowLabel(report.previousStart, report.previousEnd)}`
}
